/**
 * Ключи кэша с ресурсом (D-064, R07, F06).
 *
 * У каждого запроса native-ресурса `resourceId` — АРГУМЕНТ endpoint, а не
 * скрытая глобальная настройка: RTK Query строит ключ кэша из аргументов, и
 * два ресурса с одинаковым товаром получают два разных ключа. Теги — так же:
 * `<rid>:<id>`, и сброс одного AQSI не стирает второй.
 */

/**
 * Общий тег области ресурса. Его отдаёт каждый ресурсный endpoint, поэтому
 * `invalidateResourceScope(rid)` перечитывает всё, что относится к ресурсу, и
 * ничего — у соседнего.
 */
export const RESOURCE_SCOPE_TAG = 'ResourceScope' as const

/** Аргументы любого ресурсного запроса: rid обязателен. */
export interface ResourceScopedArgs {
  resourceId: string
}

export interface ResourceTag<T extends string> {
  type: T
  id: string
}

/**
 * rid в ключах без учёта регистра: GUID из адреса окна, из ответа службы и из
 * списка Platform пишутся по-разному, а тег и ключ обязаны совпасть — иначе
 * `resourceScopeCleared` с rid в другом регистре не сбросит кэш ресурса.
 */
export function normalizeResourceId(resourceId: string): string {
  return resourceId.trim().toLowerCase()
}

/** Один ли это ресурс (без учёта регистра и пробелов по краям). */
export function sameResourceId(left: string | null | undefined, right: string | null | undefined): boolean {
  if (left === null || left === undefined || right === null || right === undefined) return false
  return normalizeResourceId(left) === normalizeResourceId(right)
}

/** Тег сущности ресурса: `{ type, id: '<rid>:<entityId>' }`, rid в нижнем регистре. */
export function resourceTag<T extends string>(type: T, resourceId: string, entityId: string): ResourceTag<T> {
  return { type, id: `${normalizeResourceId(resourceId)}:${entityId}` }
}

/** Тег списка ресурса: `{ type, id: '<rid>:LIST' }`. */
export function resourceListTag<T extends string>(type: T, resourceId: string): ResourceTag<T> {
  return resourceTag(type, resourceId, 'LIST')
}

/** Тег области ресурса — добавляется к тегам каждого ресурсного запроса. */
export function resourceScopeTag(resourceId: string): ResourceTag<typeof RESOURCE_SCOPE_TAG> {
  return { type: RESOURCE_SCOPE_TAG, id: normalizeResourceId(resourceId) }
}

/**
 * Сегмент пути с rid. Кодирование здесь, а не в каждом `urls.ts`: rid из адреса
 * окна — ввод человека, и неэкранированный `/` увёл бы запрос в чужой маршрут.
 * rid нормализуется так же, как в тегах: один ресурс — один адрес.
 */
export function ridSegment(resourceId: string): string {
  if (resourceId.trim() === '') throw new Error('resourceId обязателен: ресурсный запрос без rid запрещён (D-064)')
  return encodeURIComponent(normalizeResourceId(resourceId))
}
